import {useState} from "react";
import {useParams} from "react-router-dom";
import styled from "styled-components/macro";
import Header from "../../components/header";
import TopBar from "../../components/top_bar";
import SortBar from "../../components/sort_bar";
import CommunityHeader from "../../components/community_header";
import Container from "../../components/container";
import Main from '../../components/main';
import Sidebar from "../../components/sidebar";
import Footer from "../../components/footer";
import {useProfileComments} from "../../api/comment";
import {useProfilePosts} from "../../api/feed";
import {useFeedback} from "../../store/feedback";
import {useQuery} from "../../utils/router";
import {useSettings} from "../../store";
import PostList from "../../components/post_list";
import Comment from "../../components/comment";
import NavLink from "../../components/nav_link";
import Wrapper from "../../components/wrapper";
import {useProfile} from "../../api/user";
import {useCommunityStyle} from "../../api/communities";
import {useBaseCommunity} from "../../store/hooks";
import Pagination from "../../components/pagination";
import {useProfileOverview} from "../../api/content";
import Post from "../../components/post";
import HeaderData from "../../components/header_data";
import useAtBottom from "../../utils/use_at_bottom";
import ErrorMessage from "../../components/error_message";
import {themecolors} from "../../utils/themecolors";
import {isPseudoCommunity} from "../../store/communites";
import {Menu, MenuButton, MenuList} from "@chakra-ui/react";
import ProfileSortMenu from "../../components/menus/profile_sort_menu";
import useWindowDimensions from "../../utils/dimensions";
import LegacySortBar from "../../components/legacy/legacy_sort_bar";
import {ChevronDown} from "lucide-react";

const Items = styled.div``;
const Item = styled.div``;
const Manage = styled.div``;
const Sort = styled.div``;
const Loading = styled.div``;
const NotFound = styled.div``;
const CurrentSort = styled.span``;
const SortContainer = styled.div``;

const SORT_MAPPING = {
    old: 'Old',
    new: 'New',
    controversial: 'Worst',
    top: 'Top',
}

const ProfileOverview = styled(({className, community, user, expand, sort}) => {
    const loading = useFeedback();
    const { content, more, loadNextPage } = useProfileOverview(loading.channel, user, sort, community);
    const detector = useAtBottom(more && !loading.busy && loadNextPage);
    return (
        <div className={className}>
            <Main>
                {content?.map((content, index) => {
                    if (!content) {
                        // eslint-disable-next-line react/no-array-index-key
                        return <Post key={index}/>;
                    }
                    if ('comments' in content) {
                        return (
                            <Post key={content.id} post={content} show_community={community === 'win' || community === 'all' || community === 'home' || community === 'trending'} expand={expand} />
                        );
                    }
                    return (
                        <Comment key={content.id} comment={content} showCommunity={community === 'win' || community === 'all' || community === 'home'} contextual hideComments postAuthor={content.post_author}/>
                    );
                })}
                {loading.busy && (
                    <PostList
                        initialExpand={expand}
                        loading
                    />
                )}
                {detector}
                <ErrorMessage error={loading.error}/>
            </Main>
        </div>
    );
})`
  ${Post}, ${Comment} {
    border-bottom: ${({theme}) => theme.color.name === "dark" ? `1px solid ${themecolors.dark_background_5}` : theme.standard_border};
  }
  ${Post} {
    padding: 12px 12px 12px 0;
  }
  ${Loading} {
    font-size: 14px;
  }
  ${Loading} svg {
    margin: 0 8px;
  }
`;

const ProfilePosts = styled(({className, community, user, sort, expand}) => {
    const page = parseInt(useQuery().page || '1');
    const loading = useFeedback();
    const {posts} = useProfilePosts(loading.channel, user, sort, page, community);
    return (
        <div className={className}>
            <Main>
                <PostList
                    initialExpand={expand}
                    posts={posts}
                    show_community={community === 'win' || community === 'all' || community === 'home' || community === 'trending'}
                    loading={loading.busy}
                />
            </Main>
            <Pagination path={`/u/${user}?type=post&sort=${sort}&page=`} has_more page={page} />
        </div>
    );
})``;

const ProfileComments = styled(({className, community, user, sort}) => {
    const page = parseInt(useQuery().page || '1');
    const loading = useFeedback();
    const {comments} = useProfileComments(loading.channel, user, sort, page, community);
    return (
        <div className={className}>
            <Main>
                {comments.map((comment) => (
                    <Comment
                        key={comment.id}
                        comment={comment}
                        showCommunity={community === 'win' || community === 'all' || community === 'home'}
                        contextual
                        hideComments
                        postAuthor={comment.post_author}
                    />
                ))}
                {loading.busy && Array(10).fill(null).map((comment, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Comment key={index} contextual/>
                ))}
            </Main>
            <Pagination path={`/u/${user}?type=comment&sort=${sort}&page=`} has_more page={page} />
        </div>
    );
})`
  height: 100%;
  ${Comment} {
    border-bottom: ${({theme}) => theme.color.name === "dark" ? `1px solid ${themecolors.dark_background_5}` : theme.standard_border};
  }
`;

const Profile = styled(({className}) => {

    const {user} = useParams();
    const {expand} = useSettings();

    const {sort = "new", type = "overview", from = ""} = useQuery();

    const sort_name = sort ? SORT_MAPPING[sort.toLowerCase()] : 'New';
    const loading = useFeedback();
    const profile = useProfile(loading.channel, user);
    const viewable = !(profile?.is_deleted || profile?.is_suspended);
    const title = user === 'me' ? 'Your Profile' : `${profile ? profile.username : user  }'s Profile`;
    const community = useBaseCommunity();
    const style = useCommunityStyle(community);

    const [sorting, setSorting] = useState(false);
    const handleSort = () => {
        setSorting(!sorting);
    };

    const {width} = useWindowDimensions();

    return (
        <div className={className}>
            <HeaderData title={title} community={community} community_style={style} >
                {!profile?.index_profile && <meta name="robots" content="noindex, follow" />}
            </HeaderData>
            <Header>
                {(community !== "TheDonald" || width <= 780) && <TopBar legacyConversion={community === "TheDonald"} profile={profile}/>}
                {!isPseudoCommunity(community) &&
                    <>
                        <CommunityHeader community={community}/>
                    </>
                }
                {community === "TheDonald" && width > 780 && <LegacySortBar />}
            </Header>
            <SortBar community={community} />
            <Container>
                <Wrapper>
                    {!viewable ?
                        <Main>
                            <NotFound>
                                User has {profile?.is_deleted ? 'deleted their account' : 'been suspended'}.
                            </NotFound>
                        </Main>
                        :
                        <>
                            <Manage>
                                <Items>
                                    <Item className={(!type || type === 'overview') && 'active'}>
                                        <NavLink href={`/u/${user}?type=overview`}
                                                 react_route>Overview</NavLink>
                                    </Item>
                                    <Item className={type === 'post' && 'active'}>
                                        <NavLink href='?type=post' react_route>Posts</NavLink>
                                    </Item>
                                    <Item className={type === 'comment' && 'active'}>
                                        <NavLink href='?type=comment'
                                                 react_route>Comments</NavLink>
                                    </Item>
                                </Items>
                                <SortContainer>
                                        <Menu>
                                            <MenuButton>
                                                <Sort>
                                                    Sort by {sort_name}
                                                    <ChevronDown size={18} />
                                                </Sort>
                                            </MenuButton>
                                            <MenuList>
                                                <ProfileSortMenu type={type} from={from} />
                                            </MenuList>
                                        </Menu>
                                </SortContainer>
                            </Manage>
                            {(!type || type === 'overview') &&
                            <ProfileOverview community={community} user={user} expand={expand} sort={sort}/>}
                            {type === 'post' &&
                            <ProfilePosts community={community} user={user} sort={sort} expand={expand}/>}
                            {type === 'comment' && <ProfileComments community={community} user={user} sort={sort}/>}
                        </>
                    }
                </Wrapper>
                <Sidebar profile={viewable && profile} sidebar={style && style.sidebar} community={community} />
            </Container>
            <Footer />
        </div>
    );
})`
  
  ${Item} {
    padding: 6px 16px;
    margin-right: 6px;
    border-radius: 48px;
    white-space: nowrap;
  }
  ${Items} {
    display: flex;
    align-items: center;
    overflow-y: scroll;
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
  ${Items}::-webkit-scrollbar {
    display: none;
  }
  
  ${Manage} {
    display: flex;
    font-size: 15px;
    margin-bottom: 12px;
  }
  
  ${Item} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }
  ${Item}.active {
    background-color: ${({theme}) => theme.color.name === "dark" ? themecolors.dark_background_2 : '#fff'};
  }
  
  ${Sort} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
    cursor: pointer;
    white-space: nowrap;
    display: flex;
    align-items: center;
  }
  ${CurrentSort} {
    padding-left: 8px;
  }
  ${Sort} svg {
    margin-left: 8px;
  }
  ${NotFound} {
    padding: 12px 16px;
    font-size: 14px;
  }
  ${SortContainer} {
    margin-left: auto;
    display: flex;
    align-items: center;
  }

  @media only screen and (max-width: 780px) {
    ${Manage} {
      width: 100%;
      margin: 2px 0;
      overflow: scroll;
      -ms-overflow-style: none;  /* IE and Edge */
      scrollbar-width: none;  /* Firefox */
    }
    ${Manage}::-webkit-scrollbar {
      display: none;
    }
    ${Items} {
      padding-left: 12px;
    }
    ${Sort} {
      padding: 12px 24px;
    }
  }
  
`;

export default Profile;
